import React, { useState, useEffect } from "react";
import {
	useParams,
	Link,
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import { Form, Input, Select, Row, Col, message, Modal, Space } from "antd";
import { InnerForm, ProductData, PublishSettings } from "./styles";
import {
	openNotificationWithIcon,
	TYPE,
} from "../../../../../utils/notificationToast";
import Main from "../../../../layout/main/Main";
import COLORS from "../../../../layout/theme/colors/Colors";
import LoadingSkeleton from "./components/loading-skeleton/LoadingSkeleton";
import UploadFileImage from "../../../../common/upload-file-with-image/UploadFileImage";
import { CloseCircleOutlined } from "@ant-design/icons";
import { get_clients_name_list } from "../../../../../api/endpoints/clients";
import {
	get_ia_plan_by_id,
	update_ia_plan,
} from "../../../../../api/endpoints/iaPlan";
import DefaultButton from "../../../../common/antd-buttons/DefaultButton";
import PrimaryButton from "../../../../common/antd-buttons/PrimaryButton";

const GUTTER = 10;

const getBase64 = (img, callback) => {
	const reader = new FileReader();
	reader.addEventListener("load", () => callback(reader.result));
	reader.readAsDataURL(img);
};

export default function UpdateIAPlan() {
	const { id } = useParams();
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");

	const [submitting, setSubmitting] = useState(false);
	const [imageUrl, setImageUrl] = useState();
	const [initialValues, setInitialValues] = useState(null);
	const [updated, setUpdated] = useState(false);
	const [clientsForIAPlan, setClientsForIAPlan] = useState(null);
	const [clients, setClients] = useState(null);
	const tokenPrice = Form.useWatch("tokenPrice", form);

	const value100k = tokenPrice ? Number(tokenPrice) * 100000 : 0;

	const fetch_ia_plan_by_id = async () => {
		const data = await get_ia_plan_by_id(id);
		const formData = {
			title: data.title,
			tokenPrice: data.tokenPrice,
			tokenQty: data.tokenQty,
			planPrice: data.planPrice,
			state: data.isActive ? "ACTIVE" : "DELETED",
			file: data.photoUrl,
		};

		setClientsForIAPlan(data.clients?.map((r) => r.id));

		setImageUrl(data.photoUrl);

		setInitialValues(formData);
	};

	const fetch_clients_list = async (term) => {
		const res = await get_clients_name_list(term);

		setClients(
			res.map((r) => ({
				label: r.name,
				value: r.id,
			})),
		);
	};

	useEffect(() => {
		if (id) {
			fetch_ia_plan_by_id();
			fetch_clients_list();
		}
	}, [updated]);

	const onFinish = async (values) => {
		try {
			setSubmitting(true);

			const postData = {
				title: values?.title,
				tokenPrice: values?.tokenPrice,
				tokenQty: values?.tokenQty,
				planPrice: values?.planPrice,
				clients: clientsForIAPlan,
			};

			const chainFormData = new FormData();

			chainFormData.append(
				"file",
				values.file?.fileList?.slice(-1)[0]?.originFileObj,
			);

			chainFormData.append("data", JSON.stringify(postData));

			await update_ia_plan(id, chainFormData);

			openNotificationWithIcon(
				TYPE.SUCCESS,
				"Plan editado con éxito",
				"El plan se editó con éxito",
			);
			setUpdated((prev) => !prev);
			// navigate(`/admin/distributors?uri=${uri}`);
		} catch (error) {
			openNotificationWithIcon(
				TYPE.ERROR,
				"Error al editar el plan",
				"Un error ocurrió al intentar editar el plan, contacte con soporte a soporte@onetransfer.com.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	const handleImageChange = (info) => {
		try {
			const isJpgOrPng =
				info.file.type === "image/jpeg" || info.file.type === "image/png";

			if (!isJpgOrPng) {
				message.error("You can only upload JPG/PNG file!");
			}

			const isLt2M = info.file.size / 1024 / 1024 < 2;

			if (!isLt2M) {
				message.error("Image must smaller than 2MB!");
			}

			if (!isJpgOrPng || !isLt2M) return;

			getBase64(
				info.fileList[info.fileList.length - 1].originFileObj,
				(url) => {
					setImageUrl(url);
				},
			);
		} catch (error) {
			console.log(error);
		}
	};

	if (!id) navigate("/admin/iaPlans?uri=" + uri);

	if (!initialValues) return <LoadingSkeleton />;

	const handleBack = () => {
		Modal.confirm({
			title: "¿Seguro que quiere volver sin guardar?",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content:
				"Si regresa sin guardar se perderán los datos que hayan sido modificados",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => {
				navigate("/admin/distributors?uri=" + uri);
			},
		});
	};

	return (
		<Main>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/" + uri}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.BreadcrumbItem>
						<Link to={"/admin/iaPlans?uri=" + uri}>Planes</Link>
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>Editar plan</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>

			<Main.Body>
				<Form
					form={form}
					name="basic"
					onFinish={onFinish}
					autoComplete="off"
					requiredMark={false}
					layout="vertical"
					initialValues={initialValues}
				>
					<InnerForm>
						<ProductData>
							<Space
								direction="vertical"
								size="middle"
								style={{ display: "flex" }}
							>
								<Row gutter={GUTTER}>
									<Col span={6}>
										<Form.Item
											name="file"
											label={
												<label
													style={{ color: COLORS.Primary, fontWeight: "600" }}
												>
													Logo del plan
												</label>
											}
											rules={[]}
										>
											<UploadFileImage
												onChange={handleImageChange}
												urlImage={imageUrl}
												imageHeight="100%"
												imageWidth="100%"
											/>
										</Form.Item>
									</Col>
									<Col span={18}>
										<Row gutter={GUTTER}>
											<Col span={12}>
												<Form.Item
													label={
														<label
															style={{
																color: COLORS.Primary,
																fontWeight: "600",
															}}
														>
															Nombre
														</label>
													}
													name="title"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
														{
															pattern: new RegExp(
																/^(?![-.\(\)\s]*$)[-.\(\)a-zA-ZÀ-ÿ0-9\s]*$/,
															),
															message:
																"No se aceptan caracteres especiales. Solo letras y numeros",
														},
													]}
												>
													<Input placeholder="Nombre" />
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													label={
														<div
															style={{
																display: "flex",
																alignItems: "center",
																justifyContent: "space-between",
																gap: 30,
															}}
														>
															<label
																style={{
																	color: COLORS.Primary,
																	fontWeight: "600",
																}}
															>
																Precio Token
															</label>
															<div
																style={{
																	fontSize: 13,
																	color: "#666",
																}}
															>
																(100k tokens:{" "}
																<strong>{value100k.toFixed(2)} USD</strong>)
															</div>
														</div>
													}
													name="tokenPrice"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
													]}
												>
													<Input
														type="number"
														placeholder="Precio token"
														min={0}
														step={0.00001}
														style={{ width: "100%" }}
														addonAfter="USD"
													/>
												</Form.Item>{" "}
											</Col>
										</Row>
										<Row gutter={GUTTER}>
											<Col span={12}>
												<Form.Item
													label={
														<label
															style={{
																color: COLORS.Primary,
																fontWeight: "600",
															}}
														>
															Limite Tokens
														</label>
													}
													name="tokenQty"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
													]}
												>
													<Input
														type="number"
														placeholder="Limite token"
														min={1}
														style={{ width: "100%" }}
														addonAfter="un."
													/>
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													label={
														<label
															style={{
																color: COLORS.Primary,
																fontWeight: "600",
															}}
														>
															Precio Plan
														</label>
													}
													name="planPrice"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
													]}
												>
													<Input
														type="number"
														placeholder="Precio plan"
														min={1}
														style={{ width: "100%" }}
														addonAfter="USD"
													/>
												</Form.Item>
											</Col>
										</Row>
										<Row gutter={GUTTER}>
											<Col span={24}>
												<Form.Item
													label={
														<label
															style={{
																color: COLORS.Primary,
																fontWeight: "600",
															}}
														>
															Clientes
														</label>
													}
													rules={[]}
												>
													<Select
														showSearch
														mode="multiple"
														placeholder="Buscar cliente"
														optionFilterProp="children"
														onChange={(e) => setClientsForIAPlan(e)}
														value={
															clientsForIAPlan?.length > 0
																? clientsForIAPlan
																: []
														}
														filterOption={(input, option) =>
															(option?.label ?? "")
																.toLowerCase()
																.includes(input.toLowerCase())
														}
														options={clients}
													/>
												</Form.Item>
											</Col>
										</Row>
									</Col>
								</Row>
							</Space>
						</ProductData>
						<PublishSettings>
							<Space
								direction="vertical"
								size="middle"
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Form.Item>
									<PrimaryButton
										htmlType="submit"
										loading={submitting}
										style={{ width: 220 }}
									>
										Guardar
									</PrimaryButton>
								</Form.Item>
								<DefaultButton
									loading={submitting}
									onClick={handleBack}
									style={{
										border: `1px solid ${COLORS.MediumGrey}`,
										width: 220,
									}}
								>
									Volver sin guardar
								</DefaultButton>
							</Space>
						</PublishSettings>
					</InnerForm>
				</Form>
			</Main.Body>
		</Main>
	);
}
